import type { PriorityOption } from "@/models/priority.model";
import { usePriorityRepository } from "@/shared/contexts/repository-context";
import { getGetPriorityOptionsQueryKey } from "@/spz-api/priorities/priorities";
import { Checkbox } from "@spz/ui/components/checkbox";
import { PriorityBadge } from "@spz/ui/components/priority-badge";
import { SearchableSelect } from "@spz/ui/components/searchable-select";
import type { OptionRendererProps } from "@spz/ui/components/searchable-select";
import { useQuery } from "@tanstack/react-query";
import type { Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import type { ControlResultWithRelationships } from "../hooks/use-control-results-columns";

const PriorityOptionRenderer = ({
  option,
  isSelected,
  onSelect,
  readonly,
}: OptionRendererProps<string>) => {
  const priorityOption = option as PriorityOption;

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          className="mr-1"
        />
        <PriorityBadge
          id={priorityOption.value}
          variant={priorityOption.variant}
          symbol={priorityOption.symbol}
          label={priorityOption.label}
          showLabel={true}
          disabled={readonly}
        />
      </div>
    </div>
  );
};

export const ControlResultPriorityFilter = ({
  table,
}: {
  table: Table<ControlResultWithRelationships>;
}) => {
  const { t } = useTranslation();
  const rawValue = table.getColumn("priority_uuid")?.getFilterValue();

  const priorityRepository = usePriorityRepository();

  const { data: options = [], isLoading } = useQuery({
    queryKey: getGetPriorityOptionsQueryKey(),
    queryFn: () => priorityRepository.getPriorityOptions(),
  });

  const onFilterChange = (selectedPriorities: string[]) => {
    table.setColumnFilters((current) => {
      const filtered = current.filter((f) => f.id !== "priority_uuid");
      if (selectedPriorities.length > 0) {
        return [
          ...filtered,
          { id: "priority_uuid", value: selectedPriorities },
        ];
      }
      return filtered;
    });
  };

  return (
    <SearchableSelect
      placeholder={t("priority")}
      options={options}
      value={(rawValue as string[]) ?? []}
      onSelect={onFilterChange}
      multi
      variant="ghost"
      isLoading={isLoading}
      optionRenderer={PriorityOptionRenderer}
    />
  );
};
